<template>
  <div class="s-homepage-banner">
    <div class="s-homepage-banner__content">
      <SHeading class="s-homepage-banner__title" level="1" size="1">
        {{ tr("client.homepage.home_title") }}
      </SHeading>
      <div class="s-homepage-banner__search-wrapper">
        <div class="s-homepage-banner__product-search">
          <ProductSearch
            :key="isLocationSearchEnabled ? 'location' : 'default'"
            :button-label="
              tr('client.homepage.product_search_looking_for_button')
            "
            :label="tr('client.homepage.product_search_looking_for_label')"
            :placeholder="tr('client.homepage.header_looking_for_placeholder')"
            prefix-icon="far fa-search"
            :resolve-target="
              isLocationSearchEnabled ? resolveTarget : undefined
            "
          >
            <STextInput
              v-if="isLocationSearchEnabled"
              v-model="zipCode"
              autocomplete="postal-code"
              class="s-product-search__field"
              :label="tr('client.homepage.product_search_zip_code_label')"
              name="zip_code"
              :placeholder="tr('client.homepage.zip_code_placeholder')"
              :rules="[zipcodeRule(locale)]"
              :show-valid-state="false"
              :validation-events="onCreatedIfValue(eager)"
            />
          </ProductSearch>
        </div>
        <div class="s-homepage-banner__divider" />
        <p class="s-homepage-banner__login-prompt">
          {{ tr("client.homepage.product_search_login_prompt") }}
          <a
            class="s-homepage-banner__login-link"
            :href="ziggy('auth.login', undefined, { project: 'mySolvari' })"
          >
            <strong>{{
              tr("client.homepage.product_search_login_link")
            }}</strong>
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getAbTestFeature } from "@solvari/ab-testing";
import {
  eager,
  onCreatedIfValue,
  zipcodeRule,
} from "@solvari/common-fe/validation";
import { ProductSearch, SHeading, STextInput } from "@solvari/components";
import { useI18nMainSiteHomepage } from "@solvari/translations";

import { useLocationSearchTarget } from "~/features/homepage/composables/useLocationSearchTarget";
import { useHostLocale } from "~/shared/composables/useHost";
import { ziggy } from "~/shared/libs/ziggy.ts";

const { tr } = useI18nMainSiteHomepage();
const locale = useHostLocale();
const isLocationSearchEnabled = getAbTestFeature<boolean>(
  "main-site-homepage-location-search",
  false,
);
const { zipCode, resolveTarget } = useLocationSearchTarget();
</script>

<style lang="postcss">
.s-homepage-banner {
  @apply relative h-fit;

  &__content {
    @apply relative flex flex-col items-center justify-center pt-2 gap-8;

    /* both label atoms default to gray-900; the hero card uses slate-700 */
    .s-label-atom__text,
    .s-label {
      @apply text-slate-700;
    }
  }

  &__title {
    @apply mb-0 text-center;
  }

  &__search-wrapper {
    @apply w-2xl max-w-full rounded-md border border-blue-100 bg-white p-4 shadow-md;
  }

  &__product-search {
    .s-product-search {
      @apply ml-0 max-w-full;
    }
  }

  &__divider {
    @apply mb-4 mt-2 h-px w-full bg-slate-200;
  }

  &__login-prompt {
    @apply m-0 text-center text-sm text-slate-500;
  }

  &__login-link {
    @apply block text-primary-600 no-underline;

    &:hover {
      @apply underline;
    }
  }
}

@screen 600 {
  .s-homepage-banner {
    &__content {
      @apply pt-4 gap-8;
    }

    &__search-wrapper {
      @apply max-w-full;
    }

    &__product-search {
      @apply min-h-24;
    }

    &__divider {
      @apply mt-0;
    }

    &__login-prompt {
      @apply text-left;
    }

    &__login-link {
      @apply inline;
    }
  }
}

@screen 800 {
  .s-homepage-banner {
    &__content {
      @apply pt-8;
    }

    &__title {
      @apply mb-12 text-center;
    }
  }
}

@screen 1000 {
  .s-homepage-banner {
    &__search-wrapper {
      @apply max-w-[700px];
    }

    &__product-search {
      .s-product-search {
        @apply flex-row;
      }
    }

    &__login-prompt {
      @apply text-sm;
    }
  }
}
</style>
